import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { theme } from '../styles/MUITheme';
import styled from '@emotion/styled';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';
import { 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button
} from '@mui/material';
import OrderView from '../components/OrderView';
import Modal from '@mui/material/Modal';
import { startOfDay } from 'date-fns';
import { LINE_STATUSES } from '../firebase/schema';
import { getMockupUrl } from '../utils/fileHelpers';
import { formatDate } from '../utils/dateFormatter';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { syncOrderStatusFromLines } from '../utils/orderStatusSync';
import { lineStatusAfterCut } from '../utils/lineCutStatus';
import { getScreenGroupKey } from '../utils/screenPrintBatching';

const MetricBox = styled(Box)`
  background-color: var(--al-surface);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 200px;

  .metric-value {
    font-size: 1.8em;
    font-weight: bold;
    color: var(--al-primary);
    margin-bottom: 8px;
  }

  .metric-label {
    font-size: 0.9em;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &.warning .metric-value {
    color: #FFA500;
  }

  &.danger .metric-value {
    color: #FF4C4C;
  }
`;

const CustomCheckboxContainer = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(var(--al-primary-rgb), 0.5);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin: 0 auto;

  ${props => props.checked && `
    background-color: var(--al-primary);
    border-color: var(--al-primary);
  `}

  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  &:hover {
    border-color: ${props => !props.disabled && 'var(--al-primary)'};
  }

  svg {
    width: 14px;
    height: 14px;
    fill: #222;
    opacity: ${props => props.checked ? 1 : 0};
  }
`;

const DAILY_SCREENS = 20;

const emptyProgress = () => ({ screens: 0, cuts: [] });

const ScreenList2 = () => {
  const { facilityId } = useFacility();
  const [screenList, setScreenList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [orderViewData, setOrderViewData] = useState(null);
  const [artModalOpen, setArtModalOpen] = useState(false);
  const [artModalUrl, setArtModalUrl] = useState(null);
  const [cutDialogOpen, setCutDialogOpen] = useState(false);
  const [lineToMark, setLineToMark] = useState(null);
  const [isMarking, setIsMarking] = useState(false);
  const [cutProgress, setCutProgress] = useState(emptyProgress);

  const screenToday = useMemo(() => startOfDay(new Date()), []);
  const dateKey = useMemo(() => startOfDay(new Date()).toISOString().split('T')[0], []);

  const toDateObj = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      const parsed = new Date(trimmed);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  /**
   * Group lines that can share the same physical screen set (see screenPrintBatching.js):
   * same normalized design + identical F/B/O inks, with forceSeparateScreens / screenGroupId overrides.
   */
  const buildScreenGroups = (lines) => {
    const groups = new Map();
    for (const line of lines || []) {
      const key = getScreenGroupKey(line);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          key,
          lines: [line],
          screenCount: calculateScreensForLine(line),
          dueDate: toDateObj(line.dueDate),
          orderNumber: line.orderNumber || '',
          designName: line.designName || ''
        });
      } else {
        existing.lines.push(line);
        existing.screenCount = Math.max(existing.screenCount, calculateScreensForLine(line));
        const candidateDue = toDateObj(line.dueDate);
        if (!existing.dueDate || (candidateDue && candidateDue < existing.dueDate)) {
          existing.dueDate = candidateDue;
        }
      }
    }
    return Array.from(groups.values());
  };

  const calculateScreensForLine = (line) => {
    const frontScreens = Array.isArray(line.frontInkColors) ? line.frontInkColors.length : 0;
    const backScreens = Array.isArray(line.backInkColors) ? line.backInkColors.length : 0;
    const otherScreens = Array.isArray(line.otherInkColors) ? line.otherInkColors.length : 0;
    return frontScreens + backScreens + otherScreens;
  };

  const fillScreens = (lines, maxScreens = DAILY_SCREENS) => {
    let count = 0;
    let selectedGroups = [];

    // Sort by earliest due date first at the GROUP level.
    const sortedGroups = buildScreenGroups(lines).sort((a, b) => {
      const dateA = a.dueDate || new Date(9999, 11, 31);
      const dateB = b.dueDate || new Date(9999, 11, 31);
      if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
      const aOrder = String(a.orderNumber || '');
      const bOrder = String(b.orderNumber || '');
      if (aOrder !== bOrder) return aOrder.localeCompare(bOrder);
      return String(a.designName || '').localeCompare(String(b.designName || ''));
    });

    for (let i = 0; i < sortedGroups.length; i++) {
      const group = sortedGroups[i];
      const screens = group.screenCount || 0;
      if (count + screens <= maxScreens) {
        selectedGroups.push(group);
        count += screens;
      } else {
        break;
      }
    }

    return {
      lines: selectedGroups.flatMap((g) => g.lines),
      screenCount: count,
      groups: selectedGroups
    };
  };

  const fetchScreenList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch all lines with status READY_TO_ORDER, ORDERED, or IN_NOT_CUT
      const q = query(
        collection(db, 'orderLines'),
        where('status', 'in', [
          LINE_STATUSES.READY_TO_ORDER,
          LINE_STATUSES.ORDERED,
          LINE_STATUSES.IN_NOT_CUT
        ]),
        where('type', '==', 'screen')  // Match the actual value in Firestore
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const orderIds = [...new Set(lines.map(line => line.orderId).filter(id => id))];
      const orderDocs = await Promise.all(
        orderIds.map(async orderId => {
          const orderRef = doc(db, 'orders', orderId);
          const orderSnap = await getDoc(orderRef);
          return { id: orderId, ...orderSnap.data() };
        })
      );

      const orderMap = new Map(orderDocs.map(order => [order.id, order]));

      const linesWithOrder = lines.map(line => {
        const order = orderMap.get(line.orderId);
        return {
          ...line,
          facilityId: line.facilityId || order?.facilityId,
          orderNumber: order?.poNumber || 'N/A',
          customerName: order?.customerName || 'N/A',
          dueDate: line.dueDate || order?.dueDate || null
        };
      });

      const uncut = filterByFacility(linesWithOrder, facilityId).filter(line => !line.isCut);
      setScreenList(uncut);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadProgress = async () => {
      try {
        const ref = doc(db, 'screenlist2Progress', dateKey);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data() || {};
          setCutProgress({
            screens: d.screens || 0,
            cuts: Array.isArray(d.cuts) ? d.cuts : []
          });
        }
      } catch (err) {
        console.error('Error loading ScreenList2 progress:', err);
      }
    };

    loadProgress();
    fetchScreenList();
  }, [dateKey, facilityId]);

  const creditCutLine = async (line) => {
    const screens = Math.max(0, calculateScreensForLine(line));
    const entry = {
      id: line?.id || '',
      designName: line?.designName || 'Unnamed Design',
      orderNumber: line?.orderNumber || 'N/A',
      customerName: line?.customerName || '',
      screens
    };

    let nextProgress;
    setCutProgress((prev) => {
      const base = prev || emptyProgress();
      if (base.cuts.some((c) => c.id === entry.id)) {
        nextProgress = base;
        return base;
      }
      nextProgress = {
        screens: (base.screens || 0) + screens,
        cuts: [...base.cuts, entry]
      };
      return nextProgress;
    });

    try {
      const ref = doc(db, 'screenlist2Progress', dateKey);
      await setDoc(
        ref,
        {
          dateKey,
          screens: nextProgress.screens,
          cuts: nextProgress.cuts,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error saving ScreenList2 progress:', err);
    }
  };

  // Open confirmation dialog
  const handleCutClick = (line) => {
    setLineToMark(line);
    setCutDialogOpen(true);
  };

  // Cancel marking as cut
  const handleCutCancel = () => {
    setCutDialogOpen(false);
    setLineToMark(null);
  };

  // Confirm marking as cut
  const handleCutConfirm = async () => {
    if (!lineToMark) return;
    
    setIsMarking(true);
    try {
      const lineRef = doc(db, 'orderLines', lineToMark.id);
      const newStatus = lineStatusAfterCut(lineToMark);
      await updateDoc(lineRef, {
        isCut: true,
        status: newStatus
      });
      if (newStatus !== lineToMark.status) {
        await syncOrderStatusFromLines(db, lineToMark.orderId);
      }
      
      await creditCutLine(lineToMark);

      setScreenList(prev => prev.filter(l => l.id !== lineToMark.id));
      
      setCutDialogOpen(false);
      setLineToMark(null);
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Error updating status: ' + error.message);
    } finally {
      setIsMarking(false);
    }
  };

  // Move isOverdue back outside but with better checks
  const isOverdue = (job, today) => {
    if (!job || !today || !job.dueDate) return false;
    const dueObj = toDateObj(job.dueDate);
    if (!dueObj) return false;
    const due = startOfDay(dueObj);
    return due < today;
  };

  const queueSplit = useMemo(() => {
    const { lines: todayLines, groups: todayGroups, screenCount } = fillScreens(screenList);
    const todayGroupKeys = new Set((todayGroups || []).map((g) => g.key));
    const upNextLines = screenList.filter((line) => !todayGroupKeys.has(getScreenGroupKey(line)));
    return {
      todayLines,
      upNextLines,
      suggestedScreenCount: screenCount
    };
  }, [screenList]);

  const goalPct = Math.min(100, Math.round((cutProgress.screens / DAILY_SCREENS) * 100));

  const calculateMetrics = useMemo(() => {
    if (!Array.isArray(screenList) || !screenToday) {
      return {
        totalScreens: 0,
        suggestedScreenCount: 0,
        overdueCount: 0
      };
    }

    const totalScreens = buildScreenGroups(screenList).reduce((sum, g) => sum + (g.screenCount || 0), 0);
    const overdueCount = buildScreenGroups(screenList.filter((job) => isOverdue(job, screenToday))).reduce(
      (sum, g) => sum + (g.screenCount || 0),
      0
    );

    return {
      totalScreens,
      suggestedScreenCount: queueSplit.suggestedScreenCount,
      overdueCount
    };
  }, [screenList, screenToday, queueSplit.suggestedScreenCount]);

  const handleArtModalOpen = (job) => {
    setArtModalUrl(getMockupUrl(job));
    setArtModalOpen(true);
  };

  const handleArtModalClose = () => setArtModalOpen(false);

  const groupMetaByLineId = useMemo(() => {
    const meta = new Map();
    for (const group of buildScreenGroups(screenList)) {
      for (const line of group.lines) {
        meta.set(line.id, { groupSize: group.lines.length, sharedScreens: group.screenCount });
      }
    }
    return meta;
  }, [screenList]);

  // Render table with overdue jobs in red
  const renderScreenTable = (jobs, today, title, fallbackMessage) => (
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6" sx={{ color: 'var(--al-primary)', fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      {jobs.length > 0 ? (
        <TableContainer component={Paper} sx={{ mt: 2, backgroundColor: 'var(--al-surface)', boxShadow: 'none', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '48px' }}></TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Design Name</TableCell>
                <TableCell align="center">Quantity</TableCell>
                <TableCell align="center">Colors (F/B/O)</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow
                  key={job.id}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.05)' },
                    ...(job.isCut && {
                      '& td': {
                        textDecoration: 'line-through',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }),
                    ...(isOverdue(job, today) && {
                      '& td': {
                        color: '#FF4C4C',
                        fontWeight: 700
                      }
                    })
                  }}
                  onClick={() => handleArtModalOpen(job)}
                >
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <CustomCheckboxContainer
                      checked={job.isCut}
                      disabled={job.isCut}
                      onClick={() => !job.isCut && handleCutClick(job)}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </CustomCheckboxContainer>
                  </TableCell>
                  <TableCell>{job.orderNumber}</TableCell>
                  <TableCell>{job.customerName}</TableCell>
                  <TableCell>
                    {job.designName || 'Unnamed Design'}
                    {groupMetaByLineId.get(job.id)?.groupSize > 1 ? (
                      <Typography
                        component="div"
                        sx={{ fontSize: '0.68rem', color: 'rgba(var(--al-primary-rgb), 0.8)', mt: 0.25, textTransform: 'none' }}
                      >
                        Shared screens: {groupMetaByLineId.get(job.id)?.groupSize} lines use {groupMetaByLineId.get(job.id)?.sharedScreens} screens
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="center">{job.totalQuantity}</TableCell>
                  <TableCell align="center">
                    {`${job.frontInkColors?.length || 0} / ${job.backInkColors?.length || 0} / ${job.otherInkColors?.length || 0}`}
                  </TableCell>
                  <TableCell>{formatDate(job.dueDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2 }}>
          {fallbackMessage}
        </Typography>
      )}
    </Box>
  );

  if (isLoading) {
    return (
      <MUIThemeProvider theme={theme}>
        <Box sx={{ p: 3, mt: 8 }}>
          <Typography>Loading...</Typography>
        </Box>
      </MUIThemeProvider>
    );
  }

  if (error) {
    return (
      <MUIThemeProvider theme={theme}>
        <Box sx={{ p: 3, mt: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </MUIThemeProvider>
    );
  }

  return (
    <MUIThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100%', 
        padding: '80px 20px 20px 20px',
        backgroundColor: 'var(--al-bg)',
        minHeight: '100vh'
      }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: 'var(--al-primary)' }} />
            <Typography ml={2}>Loading screen list...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="error">Error loading screen list: {error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <MetricBox>
                <div className="metric-value">{DAILY_SCREENS}</div>
                <div className="metric-label">Daily Goal</div>
              </MetricBox>

              <MetricBox>
                <div className="metric-value">{cutProgress.screens}</div>
                <div className="metric-label">Cut Today</div>
              </MetricBox>

              <MetricBox>
                <div className="metric-value">{goalPct}%</div>
                <div className="metric-label">Goal Progress</div>
              </MetricBox>

              <MetricBox>
                <div className="metric-value">{screenList.length}</div>
                <div className="metric-label">Ready Lines</div>
              </MetricBox>

              <MetricBox>
                <div className="metric-value">{calculateMetrics.suggestedScreenCount}</div>
                <div className="metric-label">Suggested Today</div>
              </MetricBox>

              <MetricBox>
                <div className="metric-value">{calculateMetrics.totalScreens}</div>
                <div className="metric-label">Total Screens to Cut</div>
              </MetricBox>

              <MetricBox className={calculateMetrics.overdueCount > 0 ? 'danger' : ''}>
                <div className="metric-value">{calculateMetrics.overdueCount}</div>
                <div className="metric-label">Overdue Screens</div>
              </MetricBox>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'var(--al-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}
                >
                  Screens Cut Today
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.03em' }}>
                  {cutProgress.screens} / {DAILY_SCREENS} screens ({goalPct}%)
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 14,
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    width: `${goalPct}%`,
                    height: '100%',
                    backgroundColor: goalPct >= 100 ? 'var(--al-primary)' : '#5c9c5c',
                    transition: 'width 0.4s ease'
                  }}
                />
              </Box>
              {cutProgress.cuts.length > 0 ? (
                <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.03em', mr: 0.5 }}
                  >
                    ✓ Cut today ({cutProgress.cuts.length}):
                  </Typography>
                  {cutProgress.cuts.map((c) => (
                    <Typography
                      key={c.id}
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.45)',
                        textDecoration: 'line-through',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em'
                      }}
                    >
                      {c.designName} ({c.screens}s)
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Box>

            {/* Mark as Cut Confirmation Dialog */}
            <Dialog open={cutDialogOpen} onClose={handleCutCancel}>
              <DialogTitle>Mark Screen as Cut?</DialogTitle>
              <DialogContent>
                <Typography sx={{ mb: 1 }}>
                  Are you sure you want to mark this screen as cut?
                </Typography>
                {lineToMark && (
                  <Box sx={{ 
                    background: 'rgba(var(--al-primary-rgb), 0.1)', 
                    p: 2, 
                    borderRadius: 1,
                    mt: 1
                  }}>
                    <Typography sx={{ fontWeight: 600 }}>{lineToMark.designName || 'Unnamed Design'}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {lineToMark.customerName} • Invoice #{lineToMark.orderNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Colors: {lineToMark.frontInkColors?.length || 0}F / {lineToMark.backInkColors?.length || 0}B / {lineToMark.otherInkColors?.length || 0}O
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCutCancel} disabled={isMarking}>No</Button>
                <Button 
                  onClick={handleCutConfirm} 
                  color="primary" 
                  variant="contained"
                  disabled={isMarking}
                >
                  {isMarking ? 'Marking...' : 'Yes, Mark as Cut'}
                </Button>
              </DialogActions>
            </Dialog>

            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid rgba(var(--al-primary-rgb), 0.45)',
                backgroundColor: 'rgba(var(--al-primary-rgb), 0.06)',
                p: 2,
                mb: 2
              }}
            >
              {renderScreenTable(
                queueSplit.todayLines,
                screenToday,
                `Today's Goal — Suggested (${queueSplit.todayLines.length})`,
                'No screens ready to cut right now.'
              )}
            </Box>

            {queueSplit.upNextLines.length > 0 ? (
              <Box sx={{ opacity: 0.92 }}>
                {renderScreenTable(
                  queueSplit.upNextLines,
                  screenToday,
                  `Up Next — Ready to Cut (${queueSplit.upNextLines.length})`,
                  ''
                )}
              </Box>
            ) : null}
          </>
        )}
      </Box>
      {orderViewOpen && orderViewData && (
        <Modal open={orderViewOpen} onClose={() => setOrderViewOpen(false)}>
          <Box sx={{ maxWidth: 900, margin: '40px auto', background: '#222', borderRadius: 2, p: 3 }}>
            <OrderView order={orderViewData} onClose={() => setOrderViewOpen(false)} />
          </Box>
        </Modal>
      )}
      {artModalOpen && artModalUrl && (
        <Modal open={artModalOpen} onClose={handleArtModalClose}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', outline: 'none', position: 'relative' }}>
            <button
              onClick={handleArtModalClose}
              style={{
                position: 'absolute',
                top: 24,
                right: 32,
                background: 'rgba(0,0,0,0.6)',
                border: 'none',
                color: 'white',
                fontSize: 28,
                cursor: 'pointer',
                zIndex: 2
              }}
              aria-label="Close"
            >
              ×
            </button>
            <img src={artModalUrl} alt="Art File" style={{ maxHeight: '80vh', maxWidth: '90vw', background: '#222', borderRadius: 8, boxShadow: '0 0 24px #000' }} />
          </Box>
        </Modal>
      )}
    </MUIThemeProvider>
  );
};

export default ScreenList2; 